import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';

import Select from '~/components/ui/Select';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { VCardPlusPhone } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';

const iconButtonSx = { width: '40px', height: '40px' };

type VCardPlusContactInfoPhoneItemProps = {
  isPendingDeleteVCardPlusPhone: boolean;
  deleteVCardPlusPhone: (phoneId: number) => void;
  phone: VCardPlusPhone;
  updateVCardPlusPhone: (params: { phoneId: number; body: any }) => void;
};

export default function VCardPlusContactInfoPhoneItem({
  isPendingDeleteVCardPlusPhone,
  deleteVCardPlusPhone,
  phone,
  updateVCardPlusPhone,
}: VCardPlusContactInfoPhoneItemProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const [phoneType, setPhoneType] = useState(phone.type);
  const [phoneTitle, setPhoneTitle] = useState(phone.title);
  const [phoneNumber, setPhoneNumber] = useState(phone.phone);

  const phoneTypesList = [
    {
      label: t('template5:phoneType.mobile'),
      value: 'CELL',
    },
    { label: t('template5:phoneType.work'), value: 'WORK' },
    { label: t('template5:phoneType.home'), value: 'HOME' },
    { label: t('template5:phoneType.fax'), value: 'FAX' },
    {
      label: t('template5:phoneType.other'),
      value: 'OTHER',
    },
  ];

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-end"
      mb={2}
      sx={{ flexDirection: isDesktop ? 'row' : 'column', gap: '16px' }}
    >
      <StyledTelephoneSelectorContainer>
        <Select
          items={phoneTypesList}
          label={t('common:telephone')}
          value={phoneType}
          onChange={(e) => {
            const { value } = e.target;

            setPhoneType(value);
            updateVCardPlusPhone({ body: { type: value }, phoneId: phone.id });
          }}
          fullWidth
        />
        {!isDesktop && (
          <StyledTelephoneMobileCloseContainer>
            <IconButton
              aria-label="delete"
              disabled={isPendingDeleteVCardPlusPhone}
              onClick={() => deleteVCardPlusPhone(phone.id)}
              sx={iconButtonSx}
            >
              <ClearIcon />
            </IconButton>
          </StyledTelephoneMobileCloseContainer>
        )}
      </StyledTelephoneSelectorContainer>
      <TextInput
        value={phoneTitle}
        onChange={(e) => setPhoneTitle(e.target.value)}
        onBlur={() => updateVCardPlusPhone({ body: { title: phoneTitle }, phoneId: phone.id })}
        inputProps={{ maxLength: 100 }}
        helperText={isMaxLength(phoneTitle, 100)}
        fullWidth
        placeholder={t('other:label')}
        sx={{ flex: 1 }}
        absoluteHelperText={isDesktop}
      />
      <TextInput
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        onBlur={() => updateVCardPlusPhone({ body: { phone: phoneNumber }, phoneId: phone.id })}
        error={!phoneNumber}
        inputProps={{ maxLength: 40 }}
        helperText={isMaxLength(phoneNumber, 40) || (!phoneNumber && t('inputs:required'))}
        fullWidth
        placeholder={`${t('common:eg')} 00000 000000`}
        sx={{ flex: 1 }}
        absoluteHelperText={isDesktop}
      />
      {isDesktop && (
        <IconButton
          aria-label="delete"
          disabled={isPendingDeleteVCardPlusPhone}
          onClick={() => deleteVCardPlusPhone(phone.id)}
          sx={iconButtonSx}
        >
          <ClearIcon />
        </IconButton>
      )}
    </Box>
  );
}

export const StyledTelephoneSelectorContainer = styled('div')(({ theme }) => ({
  flex: 0.8,
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flex: 'none',
    width: '100%',
    gap: '16px',
  },
}));

export const StyledTelephoneMobileCloseContainer = styled('div')(() => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'flex-end',
}));
